import { db } from "./firebase";
import { doc, collection, writeBatch, increment, serverTimestamp } from "firebase/firestore";
import { COLLECTIONS } from "./collections";

/**
 * XpService
 * 
 * Serviço centralizado para gerenciamento de XP (Experience Points).
 * Toda adição/remoção de XP DEVE passar por aqui para garantir:
 *   1. Incremento atômico no documento do target (member/employee)
 *   2. Registro de auditoria na subcoleção `xpLogs`
 * 
 * Coleção de logs: tenants/{idTenant}/members/{memberId}/xpLogs/{autoId}
 * 
 * actionTypes padronizados:
 *   - MEMBERSHIP_PURCHASE  → Compra de plano
 *   - CLASS_ATTENDANCE      → Presença em aula
 *   - EVALUATION_PASS       → Aprovação em avaliação
 *   - ACHIEVEMENT_REWARD    → Conquista desbloqueada
 *   - ATTENDANCE_STREAK     → Bônus por sequência de presenças
 *   - MANUAL_ADJUSTMENT     → Ajuste manual pelo admin
 */

export const XP_ACTION_TYPES = {
    MEMBERSHIP_PURCHASE: "MEMBERSHIP_PURCHASE",
    CLASS_ATTENDANCE: "CLASS_ATTENDANCE",
    EVALUATION_PASS: "EVALUATION_PASS",
    ACHIEVEMENT_REWARD: "ACHIEVEMENT_REWARD",
    ATTENDANCE_STREAK: "ATTENDANCE_STREAK",
    MANUAL_ADJUSTMENT: "MANUAL_ADJUSTMENT",
};

const XpService = {

    /**
     * Adiciona XP a um membro de forma atômica usando writeBatch.
     * Usar quando NÃO estiver dentro de uma transaction existente.
     * 
     * @param {string} idTenant
     * @param {object} params
     * @param {string} params.idMember        - ID do documento do membro
     * @param {number} params.xpAmount        - Quantidade de XP a adicionar (pode ser negativo para remoção)
     * @param {string} params.actionType      - Tipo da ação (usar XP_ACTION_TYPES)
     * @param {string} params.sourceId        - ID do documento de origem (ex: id do memberMembership)
     * @param {string} params.sourceName      - Nome legível da origem (ex: "Plano Trimestral")
     * @param {string} [params.description]   - Descrição adicional
     * @param {object} params.user            - Usuário que disparou a ação { uid, name }
     */
    addXp: async (idTenant, { idMember, xpAmount, actionType, sourceId, sourceName, description, user, isEmployee = false }) => {
        if (!idTenant || !idMember) {
            console.warn("[XpService] idTenant e idMember são obrigatórios.");
            return;
        }
        if (!xpAmount || xpAmount === 0) {
            return;
        }

        const targetCollection = isEmployee ? COLLECTIONS.EMPLOYEES : COLLECTIONS.MEMBERS;

        try {
            const batch = writeBatch(db);

            // 1. Incrementar xpTotal no documento alvo
            const memberRef = doc(db, "tenants", idTenant, targetCollection, idMember);
            batch.update(memberRef, {
                xpTotal: increment(xpAmount),
                updatedAt: serverTimestamp()
            });

            // 2. Criar registro de auditoria na subcoleção xpLogs
            const xpLogRef = doc(collection(db, "tenants", idTenant, targetCollection, idMember, COLLECTIONS.XP_LOGS));
            batch.set(xpLogRef, {
                idTenant,
                xpAmount,
                actionType,
                sourceId: sourceId || null,
                sourceName: sourceName || "",
                description: description || _buildDescription(actionType, sourceName, xpAmount),
                createdAt: serverTimestamp(),
                createdBy: user ? { uid: user.uid, name: user.name || user.displayName || user.email || "Sistema" } : { uid: "system", name: "Sistema" }
            });

            await batch.commit();

            return true;
        } catch (error) {
            console.error("[XpService] Erro ao adicionar XP:", error);
            // Não propaga o erro para não quebrar a operação principal
            return false;
        }
    },

    /**
     * Adiciona XP DENTRO de uma transaction existente.
     * Usar quando o caller já está rodando uma runTransaction (ex: SalesService).
     * 
     * @param {Transaction} transaction       - Transaction do Firestore
     * @param {string} idTenant
     * @param {object} params                 - Mesmos params do addXp
     */
    addXpInTransaction: (transaction, idTenant, { idMember, xpAmount, actionType, sourceId, sourceName, description, user, isEmployee = false }) => {
        if (!idTenant || !idMember || !xpAmount || xpAmount === 0) return;

        const targetCollection = isEmployee ? COLLECTIONS.EMPLOYEES : COLLECTIONS.MEMBERS;

        // 1. Incrementar xpTotal no documento alvo
        const memberRef = doc(db, "tenants", idTenant, targetCollection, idMember);
        transaction.update(memberRef, {
            xpTotal: increment(xpAmount),
            updatedAt: serverTimestamp()
        });

        // 2. Criar registro de auditoria na subcoleção xpLogs
        const xpLogRef = doc(collection(db, "tenants", idTenant, targetCollection, idMember, COLLECTIONS.XP_LOGS));
        transaction.set(xpLogRef, {
            idTenant,
            xpAmount,
            actionType,
            sourceId: sourceId || null,
            sourceName: sourceName || "",
            description: description || _buildDescription(actionType, sourceName, xpAmount),
            createdAt: serverTimestamp(),
            createdBy: user ? { uid: user.uid, name: user.name || user.displayName || user.email || "Sistema" } : { uid: "system", name: "Sistema" }
        });
    },

    /**
     * Adiciona XP DENTRO de um writeBatch existente.
     * Usar quando o caller já abriu um batch (ex: AttendanceService).
     * 
     * @param {WriteBatch} batch              - WriteBatch do Firestore
     * @param {string} idTenant
     * @param {object} params                 - Mesmos params do addXp
     */
    addXpInBatch: (batch, idTenant, { idMember, xpAmount, actionType, sourceId, sourceName, description, user, isEmployee = false }) => {
        if (!idTenant || !idMember || !xpAmount || xpAmount === 0) return;

        const targetCollection = isEmployee ? COLLECTIONS.EMPLOYEES : COLLECTIONS.MEMBERS;

        // 1. Incrementar xpTotal no documento alvo
        const memberRef = doc(db, "tenants", idTenant, targetCollection, idMember);
        batch.update(memberRef, {
            xpTotal: increment(xpAmount),
            updatedAt: serverTimestamp()
        });

        // 2. Criar registro de auditoria na subcoleção xpLogs
        const xpLogRef = doc(collection(db, "tenants", idTenant, targetCollection, idMember, COLLECTIONS.XP_LOGS));
        batch.set(xpLogRef, {
            idTenant,
            xpAmount,
            actionType,
            sourceId: sourceId || null,
            sourceName: sourceName || "",
            description: description || _buildDescription(actionType, sourceName, xpAmount),
            createdAt: serverTimestamp(),
            createdBy: user ? { uid: user.uid, name: user.name || user.displayName || user.email || "Sistema" } : { uid: "system", name: "Sistema" }
        });
    },
};

/**
 * Gera uma descrição automática para o log de XP.
 */
function _buildDescription(actionType, sourceName, xpAmount) {
    const prefix = xpAmount > 0 ? "+" : "";
    switch (actionType) {
        case XP_ACTION_TYPES.MEMBERSHIP_PURCHASE:
            return `${prefix}${xpAmount} XP pela compra do plano "${sourceName}"`;
        case XP_ACTION_TYPES.CLASS_ATTENDANCE:
            return `${prefix}${xpAmount} XP por presença em aula`;
        case XP_ACTION_TYPES.EVALUATION_PASS:
            return `${prefix}${xpAmount} XP por aprovação em avaliação`;
        case XP_ACTION_TYPES.ACHIEVEMENT_REWARD:
            return `${prefix}${xpAmount} XP por conquista "${sourceName}"`;
        case XP_ACTION_TYPES.ATTENDANCE_STREAK:
            return `${prefix}${xpAmount} XP por sequência de presenças`;
        case XP_ACTION_TYPES.MANUAL_ADJUSTMENT:
            return `${prefix}${xpAmount} XP — ajuste manual`;
        default:
            return `${prefix}${xpAmount} XP`;
    }
}

export default XpService;
